import {
   Alert,
   AlertIcon,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalBody,
   ModalCloseButton,
   useDisclosure,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { _js } from '@ifixit/localize';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons/faPenToSquare';
import { FaIcon } from '@ifixit/icons';
import { ButtonStyles } from '@ifixit/ui/components/button';
import { useMediaQuery } from '@ifixit/ui/hooks/use-media-query';

// Type definitions
interface StatusNotification {
   type: 'success' | 'info' | 'warning' | 'error';
   message: string;
}

interface UserAction {
   id: string;
   text: string;
   url: string;
}

interface MiscLink {
   text: string;
   url: string;
}

interface Team {
   name: string;
   url: string;
   avatar: string;
}

interface UserStats {
   reputation: number;
   answers: number;
   guides: number;
}

type ConditionalDisplaysType = {
   statusNotifications: StatusNotification[];
   userBio: string;
   miscLink?: MiscLink | null;
   actions: UserAction[];
   editUrl?: string;
};

export type UserSummaryType = {
   name: string;
   username: string;
   avatarLink: string;
   avatarSrc: string;
   stats: UserStats;
   dateJoined: string;
   teams: Team[];
};

export function UserCard({
   summaryData,
   conditionalData,
   ...userData
}: {
   summaryData?: UserSummaryType;
   conditionalData?: ConditionalDisplaysType;
} & Partial<UserSummaryType>) {
   const data = summaryData || userData;
   const isMobile = useMediaQuery('(max-width: 767px)');

   return (
      <div className="flex flex-col flex-shrink-0 p-4 min-md:pt-5 bg-gray-100 rounded-[20px] gap-4 self-start w-full">
         {isMobile ? <MobileUserSummary {...data} /> : <DesktopUserSummary {...data} />}
         {conditionalData && <ConditionalDisplays {...conditionalData} />}
      </div>
   );
}

const AvatarImage = ({ src, alt, className }: { src: string; alt: string; className?: string }) => {
   const { isOpen, onOpen, onClose } = useDisclosure();
   const largeImageSrc = src.replace('.standard', '.large');

   return (
      <>
         <img className={`cursor-pointer ${className}`} src={src} alt={alt} onClick={onOpen} />
         <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
            <ModalOverlay />
            <ModalContent bg="transparent" boxShadow="none">
               <ModalCloseButton color="white" bg="blackAlpha.600" rounded="full" />
               <ModalBody p={0}>
                  <img className="rounded-lg w-full h-auto" src={largeImageSrc} alt={alt} />
               </ModalBody>
            </ModalContent>
         </Modal>
      </>
   );
};

const MobileUserSummary = ({
   name = '',
   username = '',
   avatarSrc = '',
   teams = [],
}: Partial<UserSummaryType>) => {
   const showTeams = teams.length > 0;

   return (
      <div className="flex gap-2">
         <AvatarImage src={avatarSrc} alt={name} className="rounded-full size-[72px]" />
         <div className="flex flex-col">
            <h1
               id="aboutBoxName"
               className="text-base font-semibold text-brand-500 m-0 leading-none"
            >
               {name}
            </h1>
            {username && <div className="text-gray-600 text-sm">@{username}</div>}
            {showTeams && <TeamAvatars teams={teams} />}
         </div>
      </div>
   );
};

const DesktopUserSummary = ({
   name = '',
   username = '',
   avatarSrc = '',
   stats = { reputation: 0, answers: 0, guides: 0 },
   dateJoined = '',
   teams = [],
}: Partial<UserSummaryType> & { stats?: UserStats }) => {
   const showTeams = teams.length > 0;

   return (
      <div className="flex min-md:flex-col gap-2 md:gap-4 max-md:items-center">
         <div className="flex items-end w-fit">
            <AvatarImage src={avatarSrc} alt={name} className="rounded-full size-[72px]" />
            {showTeams && <TeamAvatars teams={teams} />}
         </div>
         <div>
            <h1
               id="aboutBoxName"
               className="text-base font-semibold text-brand-500 m-0 leading-none"
            >
               {name}
            </h1>
            {username && <div className="text-gray-600 text-sm">@{username}</div>}
         </div>
         <div className="max-md:hidden">
            <UserStats {...stats} dateJoined={dateJoined} />
         </div>
      </div>
   );
};

const StatItem = ({
   label,
   value,
   id,
}: {
   label: string;
   value: string | number;
   id?: string;
}) => (
   <div className="flex gap-1">
      <span>{label}:</span>
      <span className="font-semibold" id={id}>
         {value}
      </span>
   </div>
);

const UserStats = ({
   reputation,
   answers,
   guides,
   dateJoined,
}: {
   reputation: number;
   answers: number;
   guides: number;
   dateJoined: string;
}) => {
   const stats = [
      { label: _js('Reputation'), value: reputation, show: reputation > 0, id: 'numReputation' },
      { label: _js('Member Since'), value: dateJoined, show: !!dateJoined },
      { label: _js('Solutions'), value: answers, show: answers > 0 },
      { label: _js('Guides'), value: guides, show: guides > 0 },
   ];

   return (
      <>
         {stats.map(
            ({ label, value, show, id }) =>
               show && <StatItem key={label} label={label} value={value} id={id} />
         )}
      </>
   );
};

const ConditionalDisplays = ({
   statusNotifications = [],
   userBio = '',
   miscLink,
   actions = [],
   editUrl,
}: ConditionalDisplaysType) => {
   const showStatuses = statusNotifications.length > 0;
   const showUserBio = userBio.length > 0;
   const showActions = actions.length > 0;

   return (
      <div className="flex flex-col gap-4">
         {showStatuses && <StatusNotifications statuses={statusNotifications} />}
         {showUserBio && (
            <div
               className="userBio max-md:hidden [&_p]:m-0"
               dangerouslySetInnerHTML={{ __html: userBio }}
            />
         )}
         {miscLink && (
            <div className="max-md:hidden">
               <MiscLink {...miscLink} />
            </div>
         )}
         {showActions && (
            <div className="max-md:hidden">
               <Actions actions={actions} />
            </div>
         )}
         {editUrl && <EditProfileButton editUrl={editUrl} />}
      </div>
   );
};

const StatusNotifications = ({ statuses }: { statuses: StatusNotification[] }) => (
   <div>
      {statuses.map(status => (
         <Alert key={status.message} status={status.type} my={1} py={1}>
            <AlertIcon />
            <p className="m-0">{status.message}</p>
         </Alert>
      ))}
   </div>
);

const TeamAvatars = ({ teams = [] }: { teams: Team[] }) => (
   <div className="flex min-md:-ml-[18px] max-md:mt-auto">
      {/* Cap displayed team avatars at 7 to avoid overflow */}
      {teams.slice(0, 7).map(team => (
         <a href={team.url} key={team.name} className="not-first:ml-[-4px] md:not-first:ml-[-8px]">
            <img
               className="size-7 md:size-8 rounded-full outline-2 outline-gray-100"
               src={team.avatar}
               alt={team.name}
            />
         </a>
      ))}
   </div>
);

const MiscLink = ({ url, text }: { url: string; text: string }) => (
   <a className="flex items-start gap-1 text-brand-500 py-0.5" href={url}>
      <FontAwesomeIcon icon={faCircleInfo} className="translate-y-[3px]" />
      {text}
   </a>
);

const Actions = ({ actions }: { actions: UserAction[] }) => (
   <>
      {actions.map(action => (
         <a
            className="flex items-start gap-1 text-brand-500 py-0.5"
            href={action.url}
            data-testid={action.id}
            key={action.text}
         >
            <FontAwesomeIcon icon={faCircleInfo} className="translate-y-[3px]" />
            {action.text}
         </a>
      ))}
   </>
);

const EditProfileButton = ({ editUrl }: { editUrl: string }) => (
   <a href={editUrl} className={ButtonStyles({ variant: 'white' })}>
      <FaIcon icon={faPenToSquare} />
      <span>{_js('Edit Profile')}</span>
   </a>
);
